/**
 * Auto-filter for event submissions: swear words and aggressive language.
 * Replaces prohibited words with *** and tones down aggression (caps, punctuation).
 */

const SWEAR_WORDS = new Set([
  'arse', 'bloody', 'bugger', 'crap', 'damn', 'hell', 'shit', 'shitty', 'bullshit',
  'arsehole', 'bastard', 'bollocks', 'dick', 'dickhead', 'wanker', 'sod',
  'ass', 'asshole', 'bitch', 'bs', 'wtf', 'stfu', 'fuck', 'fucking', 'fucked',
  'fucker', 'fck', 'fcking', 'fk', 'cunt', 'cock', 'prick', 'dumbass',
  'retard', 'retarded', 'rape', 'rapist', 'nazi', 'nigga', 'nigger', 'fag',
  'faggot', 'whore', 'slut', 'piss', 'pissed', 'piss off', 'pissed off',
  'screw', 'screwed', 'screw you', 'sod off', 'piss off', 'pissed off',
]);

const AGGRESSION_PATTERNS: { pattern: RegExp; replace: string | ((match: string, ...groups: string[]) => string) }[] = [
  { pattern: /!{3,}/g, replace: '!' },
  { pattern: /\?{3,}/g, replace: '?' },
  { pattern: /\.{4,}/g, replace: '...' },
  { pattern: /\b([A-Z]{4,})\b/g, replace: (_: string, word: string) => word.charAt(0) + word.slice(1).toLowerCase() },
];

const THREAT_LIKE = [
  /\b(kill|murder|hurt|attack|destroy|burn)\s+(you|u|them|her|him)\b/gi,
  /\b(i('ll|ll)\s+)?(kill|hurt|attack)\s+you\b/gi,
  /\b(die|drop\s+dead)\b/gi,
];

/**
 * Replaces swear words (whole-word, case-insensitive) with ***.
 */
function filterSwearWords(text: string): string {
  const words = text.split(/\b/);
  return words
    .map((w) => {
      const lower = w.toLowerCase().replace(/[^\w']/g, '');
      if (lower && SWEAR_WORDS.has(lower)) return '***';
      return w;
    })
    .join('');
}

/**
 * Tones down aggressive formatting: excess punctuation, sustained ALL CAPS.
 */
function toneDownAggression(text: string): string {
  let out = text;
  for (const { pattern, replace } of AGGRESSION_PATTERNS) {
    out = typeof replace === 'string'
      ? out.replace(pattern, replace)
      : out.replace(pattern, replace);
  }
  return out;
}

/**
 * Returns true if text still contains threat-like phrases (we block submission).
 */
function hasThreatLikeContent(text: string): boolean {
  const combined = `${text}`.toLowerCase();
  return THREAT_LIKE.some((re) => re.test(combined));
}

export interface FilterResult {
  /** Sanitized title */
  title: string;
  /** Sanitized description or null */
  description: string | null;
  /** Sanitized location or null */
  location: string | null;
  /** True if threat-like content was found; submission should be blocked */
  blocked: boolean;
  /** Human-readable reason when blocked */
  blockReason?: string;
}

/**
 * Runs swear-word and aggression filter on event fields.
 * If threat-like content is detected, blocked is true and blockReason is set.
 */
export function filterEventContent(
  title: string,
  description: string | null,
  location: string | null
): FilterResult {
  const rawTitle = (title ?? '').trim();
  const rawDesc = (description ?? '').trim() || null;
  const rawLoc = (location ?? '').trim() || null;

  const combined = [rawTitle, rawDesc, rawLoc].filter(Boolean).join(' ');
  if (hasThreatLikeContent(combined)) {
    return {
      title: filterSwearWords(toneDownAggression(rawTitle)),
      description: rawDesc ? filterSwearWords(toneDownAggression(rawDesc)) : null,
      location: rawLoc ? filterSwearWords(toneDownAggression(rawLoc)) : null,
      blocked: true,
      blockReason: 'Your submission contains language we cannot accept. Please remove threatening or aggressive content.',
    };
  }

  return {
    title: filterSwearWords(toneDownAggression(rawTitle)),
    description: rawDesc ? filterSwearWords(toneDownAggression(rawDesc)) : null,
    location: rawLoc ? filterSwearWords(toneDownAggression(rawLoc)) : null,
    blocked: false,
  };
}
